import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats } from '@/mocks/siteData';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '91.com',
  url: 'https://91.com',
  description: '91.com 是备受用户关注的综合性网络平台，提供官方入口导航、核心功能介绍、使用指南及常见问题解答。',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@91.com',
    availableLanguage: ['Chinese'],
  },
};

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 91.com',
  description: '了解91.com的品牌故事、平台信誉、联系方式及服务承诺。',
  url: 'https://91.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '91.com',
    url: 'https://91.com',
  },
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9pio6hn2trhb35gvg8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || parsed?.message as string
          || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Page Header */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <div className="mb-4">
                <Breadcrumb items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]} />
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-6">关于我们</h1>
              <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">
                了解91.com导航平台背后的故事与使命
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-12 md:py-16">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
                <div className="w-full md:w-1/3 flex-shrink-0">
                  <div className="relative overflow-hidden rounded-lg h-48 md:h-64">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20orange%20and%20cream%20toned%20abstract%20geometric%20modern%20office%20interior%20with%20clean%20minimal%20design%2C%20soft%20natural%20lighting%2C%20professional%20atmosphere%2C%20network%20connection%20theme%2C%20warm%20wood%20textures%20and%20white%20surfaces%2C%20harmonious%20composition&width=600&height=500&seq=91-about-brand&orientation=portrait"
                      alt="91.com 品牌形象"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">品牌介绍</h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    91.com 导航平台致力于为广大用户提供最全面、最准确的网络平台导航服务。我们深知在信息爆炸的时代，找到正确、安全的官方入口对于每位用户来说至关重要。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    自成立以来，我们始终坚持以用户需求为导向，持续更新和维护平台内容，确保每一条信息都经过严格审核，每一个链接都指向官方正规渠道。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    我们的团队由一群热爱互联网、专注用户体验的专业人士组成，致力于为用户打造一个安全、便捷、高效的信息导航平台。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">平台数据</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-6 rounded-lg bg-background-50">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation / Trust */}
          <section className="py-12 md:py-16">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">信誉与承诺</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="p-6 rounded-lg bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">信息真实性保障</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    所有导航链接均指向官方渠道，定期验证更新，确保信息的准确性和时效性。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-user-heart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">用户至上原则</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    以用户体验为核心，持续优化平台功能，快速响应用户反馈，不断提升服务品质。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-lock-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">隐私安全保障</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    严格保护用户隐私数据，不收集非必要信息，采用行业标准安全措施防护数据安全。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-service-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">持续服务承诺</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    长期稳定运营，持续更新内容，确保用户始终能够通过本平台获取91.com的最新信息。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">联系我们</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">联系方式</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-600">support@91.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">工作时间</p>
                        <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-share-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">社交媒体</p>
                        <div className="flex gap-3 mt-1.5">
                          <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="微博">
                            <i className="ri-weibo-line"></i>
                          </a>
                          <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="微信">
                            <i className="ri-wechat-line"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">发送消息</h3>
                  <form data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <input type="text" name="name" placeholder="您的姓名" className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" />
                    </div>
                    <div>
                      <input type="email" name="email" placeholder="您的邮箱" className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" />
                    </div>
                    <div>
                      <textarea name="message" rows={4} placeholder="请输入您的消息（500字以内）" maxLength={500} className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"></textarea>
                    </div>
                    <div className="anti-spam-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button type="submit" className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      发送消息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">消息已成功发送，我们将尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}